/**
 * Toggle — the on/off switch used by the settings notification rows.
 *
 * Accessibility is the whole point of this component: a real
 * `<button role="switch">` with `aria-checked`, so the state is announced and
 * it is keyboard-operable (Enter/Space come free from the native button). State
 * is never conveyed by colour alone — the knob POSITION is the primary signal,
 * the row beside it prints the state as a word, and assistive tech reads
 * `aria-checked`. The visible track is 46×28 with a transparent pad lifting the
 * hit area past 44px in both axes.
 *
 * 2026-08 counter revamp: the off state used to be a `--g-hairline` track with
 * a white knob, which in dark mode was a near-invisible smudge on a near-black
 * row, and in light mode read as "disabled" rather than "off". It is now a
 * SUNKEN well with a drawn rule around it — the same recessed treatment the
 * slip uses everywhere else — so "off" looks like an empty slot and "on" looks
 * like a filled one in both themes. The knob keeps its own rule so it stays
 * legible against both the well and the primary fill.
 */
import type { ReactElement } from "react";

export interface ToggleProps {
  /** Current on/off state. */
  on: boolean;
  /** Toggle handler. */
  onChange: (next: boolean) => void;
  /** Accessible label (the adjacent row label, for screen readers). */
  label: string;
  /** Test id for the switch button. */
  testId?: string;
}

export function Toggle({ on, onChange, label, testId }: ToggleProps): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      data-testid={testId}
      className={[
        "relative inline-flex shrink-0 items-center rounded-full p-2",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)]",
      ].join(" ")}
    >
      {/* Track — a filled slot when on, a recessed empty well when off. */}
      <span
        aria-hidden="true"
        className={[
          "relative block h-7 w-[46px] rounded-full border transition-colors duration-200 ease-[var(--g-ease)]",
          on
            ? "border-transparent bg-primary"
            : "border-[var(--g-rule)] bg-[var(--g-sunken)]",
        ].join(" ")}
      >
        {/* Knob — slides left→right; the travel IS the state signal. */}
        <span
          aria-hidden="true"
          className={[
            "absolute top-[2px] h-[22px] w-[22px] rounded-full border shadow-[var(--g-shadow-sm)]",
            "transition-[left] duration-200 ease-[var(--g-ease)] motion-reduce:transition-none",
            on
              ? "left-[21px] border-transparent bg-[var(--g-on-primary)]"
              : "left-[2px] border-[var(--g-rule)] bg-[var(--g-elevated)]",
          ].join(" ")}
        />
      </span>
    </button>
  );
}
